import React from 'react';
import { render, screen, fireEvent, waitFor } from '@testing-library/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Notepad from '../Notepad';

const theme = createTheme();

const renderWithProviders = (component: React.ReactElement) => {
  return render(<ThemeProvider theme={theme}>{component}</ThemeProvider>);
};

describe('Notepad', () => {
  const mockUpdateNote = jest.fn();

  beforeEach(() => {
    jest.clearAllMocks();
  });

  it('renders notepad with initial note', () => {
    renderWithProviders(
      <Notepad note="Initial note" updateNote={mockUpdateNote} updatingNote={false} />
    );

    const input = screen.getByPlaceholderText('Write a comment here...') as HTMLTextAreaElement;
    expect(input.value).toBe('Initial note');
  });

  it('renders empty notepad when no note provided', () => {
    renderWithProviders(<Notepad updateNote={mockUpdateNote} updatingNote={false} />);

    const input = screen.getByPlaceholderText('Write a comment here...') as HTMLTextAreaElement;
    expect(input.value).toBe('');
  });

  it('updates notepad value when typing', () => {
    renderWithProviders(<Notepad note="" updateNote={mockUpdateNote} updatingNote={false} />);

    const input = screen.getByPlaceholderText('Write a comment here...') as HTMLTextAreaElement;
    fireEvent.change(input, { target: { value: 'New note content' } });

    expect(input.value).toBe('New note content');
  });

  it('calls updateNote when save button is clicked', async () => {
    const { container } = renderWithProviders(
      <Notepad note="Test note" updateNote={mockUpdateNote} updatingNote={false} />
    );

    const saveButton = container.querySelector(
      '#candidate-save-notepad-button'
    ) as HTMLButtonElement;
    expect(saveButton).toBeInTheDocument();
    fireEvent.click(saveButton);

    await waitFor(() => {
      expect(mockUpdateNote).toHaveBeenCalledWith('Test note');
    });
  });

  it('shows loading state when updating', () => {
    const { container } = renderWithProviders(
      <Notepad note="Test note" updateNote={mockUpdateNote} updatingNote={true} />
    );

    const saveButton = container.querySelector('#candidate-save-notepad-button');
    expect(saveButton).toBeInTheDocument();
  });

  it('updates when note prop changes', () => {
    const { rerender } = renderWithProviders(
      <Notepad note="Initial" updateNote={mockUpdateNote} updatingNote={false} />
    );

    let input = screen.getByPlaceholderText('Write a comment here...') as HTMLTextAreaElement;
    expect(input.value).toBe('Initial');

    rerender(
      <ThemeProvider theme={theme}>
        <Notepad note="Updated" updateNote={mockUpdateNote} updatingNote={false} />
      </ThemeProvider>
    );

    input = screen.getByPlaceholderText('Write a comment here...') as HTMLTextAreaElement;
    expect(input.value).toBe('Updated');
  });

  it('calls onChangeCallback when provided', () => {
    const mockOnChangeCallback = jest.fn();

    renderWithProviders(
      <Notepad
        note=""
        updateNote={mockUpdateNote}
        updatingNote={false}
        onChangeCallback={mockOnChangeCallback}
      />
    );

    const input = screen.getByPlaceholderText('Write a comment here...') as HTMLTextAreaElement;
    fireEvent.change(input, { target: { value: 'New content' } });

    expect(mockOnChangeCallback).toHaveBeenCalledWith('New content');
  });

  it('uses custom inputId when provided', () => {
    const { container } = renderWithProviders(
      <Notepad
        note=""
        updateNote={mockUpdateNote}
        updatingNote={false}
        inputId="custom-notepad-input"
      />
    );

    const input = container.querySelector('#custom-notepad-input');
    expect(input).toBeInTheDocument();
  });

  it('uses custom buttonId when provided', () => {
    const { container } = renderWithProviders(
      <Notepad
        note=""
        updateNote={mockUpdateNote}
        updatingNote={false}
        buttonId="custom-save-button"
      />
    );

    const button = container.querySelector('#custom-save-button');
    expect(button).toBeInTheDocument();
  });

  it('disables primary button when primaryButtonDisabled is true', () => {
    const { container } = renderWithProviders(
      <Notepad
        note=""
        updateNote={mockUpdateNote}
        updatingNote={false}
        primaryButtonDisabled={true}
      />
    );

    const saveButton = container.querySelector(
      '#candidate-save-notepad-button'
    ) as HTMLButtonElement;
    expect(saveButton).toBeDisabled();
  });
});
